/**
 * Entrar al sistema. Dos puertas, pero no del mismo tamaño.
 *
 * U-Pasaporte es la de casi todo el mundo —estudiantes y profesores del DCC— y por eso
 * se lleva la pantalla. El usuario y la contraseña son de los profesores externos, que
 * no tienen RUT acá y estrenan su cuenta recuperando la contraseña; son pocos y saben
 * que les toca a ellos, así que su formulario va plegado detrás de un enlace en vez de
 * competir con el botón grande.
 *
 * Recuperar la contraseña vive adentro de ese pliegue por la misma razón: solo sirve a
 * quien tiene contraseña, y ofrecérsela a alguien que entra por el portal es mandarlo a
 * una pantalla que no le va a servir de nada.
 *
 * Va dentro de `PublicLayout`, así que hereda su barra y su pie.
 */
import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Link, Stack, TextField } from '@mui/material';
import { SchoolOutlined, VpnKeyOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { portalLoginUrl } from '../utils/portal';
import { TarjetaAcceso } from '../components/public/TarjetaAcceso';

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const { login, loading, error: authError } = useAuth();
  const [localError, setLocalError] = useState('');
  const navigate = useNavigate();
  const portalUrl = portalLoginUrl();

  // Sin portal configurado no hay nada que plegar: el formulario local es la única
  // entrada y se muestra de una.
  const [verLocal, setVerLocal] = useState(!portalUrl);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    setLocalError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setLocalError('Por favor, ingresa usuario y contraseña');
      return;
    }
    try {
      await login(credentials.username, credentials.password);
      navigate('/app');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setLocalError('Usuario o contraseña incorrectos. Verifica tus datos.');
      } else {
        setLocalError('No se pudo conectar con el servidor. Intenta más tarde.');
      }
    }
  };

  const displayError = localError || authError;

  return (
    // Centrada en el alto disponible entre la barra y el pie: la pantalla no tiene
    // nada más que esta tarjeta, y arrimada arriba se veía como un formulario suelto.
    <Container maxWidth="xs" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 6 }}>
      <TarjetaAcceso
        centrado
        icono={<SchoolOutlined sx={{ fontSize: 32 }} />}
        titulo="Ingresa"
        bajada="Si eres estudiante o profesor del DCC, entra con U-Pasaporte."
      >
        {displayError && (
          <Alert severity="error" sx={{ mb: 2.5, textAlign: 'left' }}>
            {displayError}
          </Alert>
        )}

        {portalUrl && (
          <Button fullWidth variant="contained" startIcon={<VpnKeyOutlined />} href={portalUrl} sx={{ py: 1.4 }}>
            Iniciar sesión con U-Pasaporte
          </Button>
        )}

        {/* El pliegue: quien no entra por el portal sabe que le toca acá. */}
        {!verLocal && (
          <Box sx={{ mt: 2.5 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="always"
              color="text.secondary"
              onClick={() => setVerLocal(true)}
            >
              Ingresar con credenciales locales
            </Link>
          </Box>
        )}

        {verLocal && (
          <Box sx={{ mt: portalUrl ? 3 : 0, textAlign: 'left' }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  name="username"
                  label="Usuario"
                  autoComplete="username"
                  autoFocus={!!portalUrl}
                  value={credentials.username}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  required
                  fullWidth
                  id="password"
                  name="password"
                  label="Contraseña"
                  type="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <Button type="submit" fullWidth variant="outlined" disabled={loading} sx={{ py: 1.25 }}>
                  {loading ? <CircularProgress size={20} /> : 'Iniciar sesión'}
                </Button>
              </Stack>
            </form>
            {/* Acá y no afuera: recuperar la contraseña solo le sirve a quien tiene una,
                y eso son los externos. */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button variant="text" size="small" onClick={() => navigate('/password/recuperar')} disabled={loading}>
                ¿Olvidaste tu contraseña?
              </Button>
            </Box>
          </Box>
        )}
      </TarjetaAcceso>
    </Container>
  );
};

export default Login;
